import { useState } from 'react'
import { SwipeEventData, SwipeableHandlers, useSwipeable } from 'react-swipeable'

const getPrevSlide = (currentSlide: number, numberOfSlides: number) => {
  const index = currentSlide - 1
  if (index < 0) {
    return numberOfSlides - 1
  }
  return index
}

const getNextSlide = (currentSlide: number, numberOfSlides: number) => {
  const index = currentSlide + 1

  if (index >= numberOfSlides) {
    return 0
  }

  return index
}

export const useImageCarousel = (numberOfSlides: number, initialSlide = 0) => {
  const [container, setContainer] = useState<any>(null)
  const [currentSlide, setCurrentSlide] = useState(initialSlide)
  const [offset, setOffset] = useState(0)
  const [preventClick, setPreventClick] = useState(false)

  const onSwiped = (
    direction: 'left' | 'right',
    currentSlide: number,
    e: SwipeEventData,
    numberOfSlides: number,
    container: HTMLElement
  ) => {
    const swipeTreshold = container.clientWidth * 0.2
    const distance = Math.abs(e.deltaX)
    // e.deltaX value is positive for scrolling left(towards prev), negative for right(towards next).
    // Limit to one swipe per change.
    if (distance >= swipeTreshold && numberOfSlides > 1) {
      // Prevent click on next and previous functions when dispatching via swipe, since we never want to activate click behaviour when swiping to new slide
      const newSlider =
        direction === 'left'
          ? getNextSlide(currentSlide, numberOfSlides)
          : getPrevSlide(currentSlide, numberOfSlides)

      if (typeof newSlider === 'number') {
        setCurrentSlide(newSlider)
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { ref, onMouseDown } = useSwipeable({
    onSwipedLeft: e => onSwiped('left', currentSlide, e, numberOfSlides, container),
    onSwipedRight: e => onSwiped('right', currentSlide, e, numberOfSlides, container),
    onSwiping: e => setOffset(e.deltaX),
    onSwiped: () => setOffset(0),
    onSwipeStart: () => {
      setPreventClick(true)
    },
    onTouchEndOrOnMouseUp: () => {
      setTimeout(() => {
        setPreventClick(false)
      }, 1000)
    },
    preventScrollOnSwipe: true,
    trackMouse: true,
    trackTouch: true,
  })

  const handlers: SwipeableHandlers = {
    onMouseDown,
    ref(refContainer: HTMLElement | null) {
      setContainer(refContainer && refContainer.firstElementChild)
      return ref(refContainer)
    },
  }

  // All but transform, width and left: Temporary test bits. Transfer to classes when ready
  const style: React.CSSProperties = {
    left: `-${(currentSlide + 1) * 100}%`, // add 1 to account for the pseudoslider at start
    width: `${100 * (numberOfSlides + 2)}%`, // add 2 to numberOfSlides to account for pseudosliders at start and end
  }

  const sliderStyle: React.CSSProperties = {
    left: `${(100 / numberOfSlides) * currentSlide}%`, // add 1 to account for the pseudoslider at start
    width: `${100 / numberOfSlides}%`, // add 2 to numberOfSlides to account for pseudosliders at start and end
  }

  if (offset !== 0) {
    style.transition = 'all 100ms ease 0s'

    style.transform = `translateX(${offset}px)`
  } else {
    style.transition = 'all 400ms ease 0s'
  }

  // nextSlide and prevSlide for buttons will not use preventClick, to not trigger the preventClick reset effect.
  return {
    currentSlide,
    desired: currentSlide,
    handlers,
    preventClick: preventClick || offset !== 0,
    style,
    sliderStyle,
    setCurrentSlide: (n: number) => setCurrentSlide(n || 0),
    nextSlide: (customSlide?: number, customNumberOfSlides?: number) =>
      setCurrentSlide(
        getNextSlide(customSlide || currentSlide, customNumberOfSlides || numberOfSlides)
      ),
    prevSlide: (customSlide?: number, customNumberOfSlides?: number) =>
      setCurrentSlide(
        getPrevSlide(customSlide || currentSlide, customNumberOfSlides || numberOfSlides)
      ),
  }
}
